import {useCallback, useEffect, useRef, useState} from 'react';
import DOMPurify from 'dompurify';
import {Bold, Italic, Underline, List} from 'lucide-react';
import {
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {cn} from '@/lib/utils';

/// Mirrors the backend's allowlist (`PRI.Shared.Text.HtmlSanitizer`) - kept in sync so what an analyst
/// pastes/previews client-side matches what the server will actually store and send. This is
/// defense-in-depth, not a replacement for server-side sanitization: it stops raw pasted HTML (which
/// can carry event handlers, `javascript:` URIs, or arbitrary markup) from ever reaching the live
/// preview's `dangerouslySetInnerHTML`, or the editable's own DOM, unsanitized.
const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'br', 'p', 'div'];

export function sanitizeRichText(html: string): string {
  return DOMPurify.sanitize(html, {ALLOWED_TAGS, ALLOWED_ATTR: []});
}

type Props = {
  readonly value: string;
  readonly onChange: (html: string) => void;
  readonly placeholder?: string;
  readonly className?: string;
};

/// contentEditable's "empty" state is often `<br>` or `<p><br></p>` rather than `''` - check for
/// leftover text before treating rich-text content as blank.
export function isRichTextEmpty(html: string): boolean {
  return richTextToPlainLines(html) === '';
}

const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'UL', 'OL']);
const HIDDEN_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE']);

function appendText(node: Node, out: string[]) {
  for (const child of node.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      out.push((child.nodeValue ?? '').replaceAll(/\s+/g, ' '));
    } else if (child instanceof Element && !HIDDEN_TAGS.has(child.tagName)) {
      if (child.tagName === 'BR') {
        out.push('\n');
      } else if (BLOCK_TAGS.has(child.tagName)) {
        out.push('\n');
        appendText(child, out);
        out.push('\n');
      } else {
        appendText(child, out);
      }
    }
  }
}

/// Plain text of an unsaved rich-text draft, one line per paragraph/list item/br, for places (the
/// amend diff) that render it as text with `whitespace-pre-wrap`. Stored notes don't come through
/// here - the API sends them already converted as `notes_text` (`PRI.Shared.Text.RichText`), and this
/// applies the same line rules so both sides of a diff agree. Parsed by the browser rather than
/// tag-stripped with a regex: a DOMParser document is inert (no scripts run, nothing loads), and only
/// its text nodes are read, so the result is never markup.
export function richTextToPlainLines(html: string): string {
  const out: string[] = [];
  appendText(new DOMParser().parseFromString(html, 'text/html').body, out);
  return out
    .join('')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .join('\n');
}

/// The toolbar, in display order. Each entry is one execCommand name, which doubles as the toggle's
/// value so the pressed set and the command set are the same list.
const FORMATS = [
  {command: 'bold', label: 'Bold', icon: Bold},
  {command: 'italic', label: 'Italic', icon: Italic},
  {command: 'underline', label: 'Underline', icon: Underline},
  {command: 'insertUnorderedList', label: 'Bulleted list', icon: List}
] as const;

type FormatCommand = (typeof FORMATS)[number]['command'];

/// Which of the toolbar's commands already apply at the caret or selection, so the toolbar can show
/// them pressed rather than leaving the analyst to guess whether the next keystroke lands bold.
/// queryCommandState is deprecated alongside execCommand and jsdom has neither, so a throwing call
/// reads as "nothing active" rather than taking the editor down.
function queryActiveFormats(): FormatCommand[] {
  return FORMATS.map((format) => format.command).filter((command) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- see exec() below
      return document.queryCommandState(command);
    } catch {
      return false;
    }
  });
}

/// A minimal rich-text editor: a contentEditable div with a bold/italic/underline/bulleted-list
/// toolbar, using the browser's own execCommand rather than pulling in an editor library. `value` is
/// only re-applied to the DOM when the field isn't focused, so typing doesn't fight external resets
/// (e.g. clearing the form after submit) with cursor-jumping.
export function RichTextArea({value, onChange, placeholder, className}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [activeFormats, setActiveFormats] = useState<FormatCommand[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== value) {
      // `value` may originate outside this component (e.g. loaded from the API) and never pass
      // through the paste handler's sanitizeRichText call - sanitize here too so the "only our
      // toolbar's tags survive in the DOM" guarantee holds regardless of where value came from.
      el.innerHTML = sanitizeRichText(value);
    }
  }, [value]);

  // The pressed state follows the caret, not the last button pressed: moving into a bold word must
  // light the Bold toggle, and moving out must clear it, which only the document's selection knows.
  // A selection outside this editor (another field, or the preview beside it) reads as nothing active.
  const refreshActiveFormats = useCallback(() => {
    const el = ref.current;
    const anchor = document.getSelection()?.anchorNode ?? null;
    const next = el && anchor && el.contains(anchor) ? queryActiveFormats() : [];
    setActiveFormats((previous) => (previous.join(',') === next.join(',') ? previous : next));
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshActiveFormats);
    return () => {
      document.removeEventListener('selectionchange', refreshActiveFormats);
    };
  }, [refreshActiveFormats]);

  const exec = (command: FormatCommand) => {
    ref.current?.focus();
    // Deprecated, but remains the only cross-browser way to drive a contentEditable toolbar without
    // pulling in an editor library - acceptable for this internal admin tool.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    document.execCommand(command);
    if (ref.current) {
      onChange(ref.current.innerHTML);
    }

    refreshActiveFormats();
  };

  const isEmpty = value.trim() === '';

  return (
    <div className={cn('border-input rounded-md border', className)}>
      <ToggleGroup
        multiple
        size="sm"
        spacing={1}
        aria-label="Formatting"
        className="border-input w-full border-b px-1.5 py-1"
        value={activeFormats}
        onValueChange={(next: string[]) => {
          // The group reports the whole pressed set; the one entry that differs from what the caret
          // already had is the toggle the analyst hit, and execCommand toggles it in the editor. The
          // editor's own state then drives the pressed set, so the group is never told a format is on
          // that the browser refused to apply.
          const toggled = FORMATS.map((format) => format.command).find(
            (command) => next.includes(command) !== activeFormats.includes(command)
          );
          if (toggled) {
            exec(toggled);
          }
        }}>
        {FORMATS.map(({command, label, icon: Icon}) => (
          <Tooltip key={command}>
            <TooltipTrigger
              render={
                <ToggleGroupItem
                  value={command}
                  aria-label={label}
                  className="size-6 min-w-0 p-0"
                  onMouseDown={(event) => {
                    // Mousedown (not click) would otherwise move focus off the contentEditable div
                    // and collapse the selection the command is meant to apply to.
                    event.preventDefault();
                  }}
                />
              }>
              <Icon />
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        ))}
      </ToggleGroup>
      <div className="relative">
        {isEmpty ? (
          <span className="text-muted-foreground pointer-events-none absolute top-1.5 left-2.5 text-xs">
            {placeholder}
          </span>
        ) : null}
        <div
          ref={ref}
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder}
          className="min-h-20 px-2.5 py-1.5 text-xs outline-none [&_li]:ml-4 [&_ul]:list-disc"
          onPaste={(event) => {
            // Pasted content can carry arbitrary HTML (event handlers, javascript: URIs, styles) from
            // outside our own toolbar - sanitize before it ever lands in the DOM, rather than trusting
            // the eventual server-side sanitize-on-save to be the only line of defense.
            event.preventDefault();
            const html = event.clipboardData.getData('text/html');
            if (html) {
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- see exec() above
              document.execCommand('insertHTML', false, sanitizeRichText(html));
            } else {
              // Plain text goes through insertText (not insertHTML) so stray `<`/`>` characters are
              // inserted literally rather than re-parsed as markup.
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- see exec() above
              document.execCommand('insertText', false, event.clipboardData.getData('text/plain'));
            }

            onChange(event.currentTarget.innerHTML);
          }}
          onInput={(event) => {
            onChange(event.currentTarget.innerHTML);
            // Typing past the end of a bold run, or Enter out of a list, changes what applies at the
            // caret without a selectionchange the toolbar would otherwise hear about.
            refreshActiveFormats();
          }}
        />
      </div>
    </div>
  );
}

/// A live rendered preview of what the outbound email will look like as the analyst types - same
/// sanitized HTML the backend will store and send, just rendered instead of edited.
export function EmailPreview({
  subject,
  bodyHtml
}: {
  readonly subject: string;
  readonly bodyHtml: string;
}) {
  return (
    <div className="border-border flex flex-col gap-2 rounded-lg border p-3">
      <span className="text-muted-foreground text-[10px] tracking-widest uppercase">
        Live Preview
      </span>
      {/* The email body is white with its own border, so it reads as a page inside the dialog's
          frame rather than blending into the popup. */}
      <div className="bg-card flex-1 rounded-md border p-3 text-xs">
        <p className="text-foreground mb-2 border-b pb-2 font-medium">
          {subject.trim() || <span className="text-muted-foreground italic">(no subject)</span>}
        </p>
        {isRichTextEmpty(bodyHtml) ? (
          <p className="text-muted-foreground text-xs italic">(empty email body)</p>
        ) : (
          /* eslint-disable react/no-danger -- sanitized just above via sanitizeRichText */
          <div
            dangerouslySetInnerHTML={{__html: sanitizeRichText(bodyHtml)}}
            className="text-foreground text-xs [&_li]:ml-4 [&_ul]:list-disc"
          />
          /* eslint-enable react/no-danger */
        )}
      </div>
    </div>
  );
}
